export const enChat = {
  title: "Ask Olive",
  inputPlaceholder: "Ask anything about your data...",
  domainSelectAria: "Select domain",
  sendAria: "Send message",
  stopButton: "Stop generating",
  processingTitle: "Processing your question...",
  processingWaiting: "Analyzing your question...",
  phases: {
    understanding: "Understanding your question",
    planning: "Planning the query",
    continuing: "Reusing your previous answer",
    querying: "Running the query",
    writing: "Data is ready — writing the insights",
    explaining: "Checking why, and what to do next",
    finishing: "Formatting the answer",
  },
  processing: {
    details: "Details",
    elapsedAria: "Time elapsed",
    reassurance:
      "Full answers usually take a minute or two; the data appears as soon as it's ready.",
  },
  assistantPlaceholder: "Analysis complete.",
  message: {
    completedAt: "Completed · {{time}}",
    cancelledAt: "Cancelled · {{time}}",
  },
  response: {
    tabs: {
      insights: "Insights",
      howGenerated: "How it was generated",
      processing: "Processing",
      table: "Table",
      data: "Table",
      chart: "Charts",
      cards: "Cards",
      sql: "SQL",
      followUps: "Follow-ups",
    },
    howGenerated: {
      request: "Request",
      response: "Response",
      answeredIn: "Answered in {{duration}}",
      timeBreakdown: "Time breakdown",
      otherTime: "Other",
      // What the turn did during each phase; `phases` above is the live label for what starts next.
      phases: {
        understanding: "Understanding your question",
        planning: "Planning the context",
        continuing: "Reusing your previous answer",
        querying: "Writing and running the query",
        writing: "Drafting and checking the answer",
        explaining: "Writing the insights and researching why",
        finishing: "Writing the answer",
        finishingAfterResearch: "Writing why and what to do next",
      },
      parallel: "Parallel",
      ranAlongside: "Ran alongside: {{steps}}",
    },
    followUps: {
      suggested: "Suggested follow-ups",
    },
    sections: {
      insights: "Insights",
      why: "Why",
      action: "Action",
      businessImpact: "Business Impact",
      sources: "Sources ({{count}})",
    },
    parts: {
      verifying: "Verifying the answer — this may still update.",
      writingInsights: "Writing the insights…",
      writingInsightsDataReady: "Writing the insights… the data is ready in the other tabs.",
      researchingWhy: "Researching why and what to do next…",
      updatedWithResearch: "Updated with research",
    },
    tables: {
      addToApp: "Add to app",
      rowCount_one: "{{count}} row",
      rowCount_other: "{{count}} rows",
      rowCountTruncated: "Showing {{shown}} of {{total}} rows",
      rowCountCut_one: "{{count}} row (truncated)",
      rowCountCut_other: "{{count}} rows (truncated)",
      resultSelector: "Result sets",
      pageOf: "{{from}}–{{to}} of {{count}}",
      resultLabel: "Result {{index}}",
      failedResults_one:
        "1 query in this turn failed, so the answer is based on partial evidence.",
      failedResults_other:
        "{{count}} queries in this turn failed, so the answer is based on partial evidence.",
    },
    cards: {
      addToApp: "Add to app",
    },
    sql: {
      multiTableBadge: "Multi-table",
      multiTableTooltip: "Combines measures across multiple tables: {{tables}}",
      multiTableTooltipGeneric:
        "Combines measures across multiple physical tables",
    },
    confidence: {
      levels: {
        high: "High confidence ({{score}}%)",
        medium: "Medium confidence ({{score}}%)",
        low: "Low confidence ({{score}}%)",
      },
      noSignals: "No validation issues detected.",
    },
    documents: {
      contextTitle: "Document context",
      sourcesTitle: "Document sources",
      agreement: {
        supports: "Documents support this answer",
        complements: "Documents add context",
        conflicts: "Documents add related detail",
        document_only: "Answered from documents",
      },
      extraInfoTitle: "Additional information from documents",
      extraInfoHelp:
        "The answer above comes from your data. Your documents also mention these figures — shown here with the file and page they came from.",
      dataSays: "Data",
      documentsSay: "Documents",
      documentStatedSource: "Document credits this to",
      likelyCause: "Likely cause",
      page: "p. {{page}}",
      pagePreviewsTitle: "Cited pages",
      expandPage: "Enlarge page",
      collapsePage: "Shrink page",
    },
    charts: {
      chartType: "Chart type",
      expand: "Expand chart",
      download: "Download chart",
      downloadPng: "Download PNG",
      downloadJpg: "Download JPG",
      close: "Close expanded chart",
      addToApp: "Add to app",
      types: {
        line: "Line",
        bar_vertical: "Vertical bar",
        bar_horizontal: "Horizontal bar",
        area: "Area",
        pie: "Pie",
        scatter: "Scatter",
        combo: "Combo",
      },
    },
    feedback: {
      thumbUpAriaLabel: "Mark this response as helpful",
      thumbDownAriaLabel: "Mark this response as not helpful",
      thumbUpTooltip: "Helpful",
      thumbDownTooltip: "Not helpful",
      commentHeadingUp: "What made this useful?",
      commentHeadingDown: "What missed the mark?",
      commentSubheading: "Optional — your feedback is already saved.",
      commentPlaceholderUp: "e.g. spotted a trend we hadn't noticed",
      commentPlaceholderDown: "e.g. the numbers look wrong",
      commentAriaLabel: "Additional feedback about this response",
      commentError: "Could not save your comment. Please try again.",
      skip: "Skip",
      submit: "Submit",
    },
  },
  clarification: {
    ask: "Ask",
    rewrite: "Rewrite",
    allOption: "All of them",
    otherOption: "Other…",
    otherPlaceholder: "Type what you meant",
    slotAria: 'Choose what "{{term}}" means',
    optionHint: {
      learning_history: "★ From your Learning History",
      intent_match: "Fits your question's intent",
      question_match: "Matches your question",
    },
  },
  learningSuggestion: {
    saveQuestion: "Save this as your learning for this domain?",
    save: "Save",
    dismiss: "No thanks",
    saved: "Saved to your learnings.",
    savedAndSuggested: "Saved to your learnings and suggested for this domain.",
    domainQuestion:
      "Also suggest it for everyone in this domain? A domain owner reviews it before it is used.",
    suggest: "Suggest for domain",
    keepPersonal: "Keep it mine",
    saveFailed: "Couldn't save your choice. Please try again.",
  },
  learningHistoryNote: {
    using: 'Using <strong>{{label}}</strong> for "{{term}}"',
    source: "from your Learning History",
    domainSource: "approved for this domain",
    change: "Change",
    changeAria: 'Change what "{{term}}" means',
  },
  errorTitle: "Something went wrong",
  terminated: {
    title: "Conversation terminated",
    message:
      "This response was stopped before it could finish. Review the generation steps to see what completed before stopping.",
    howGenerated: "Generation stopped at this point.",
  },
  backAria: "Back to chat history",
  history: {
    pageTitle: "Chat History",
    searchPlaceholder: "Search your Chats...",
    newChat: "New Chat",
    terminated: "Stopped",
    untitled: "Untitled chat",
    menuAria: "Chat options for {{title}}",
    editTitle: "Edit Title",
    delete: "Delete",
    empty: "No chats yet. Start a new chat to begin.",
    noSearchResults: "No chats match your search.",
    editTitleModal: {
      title: "Edit Title",
      label: "Chat title",
      closeAria: "Close",
      cancel: "Cancel",
      save: "Save",
      validation: {
        required: "Title is required",
      },
      errors: {
        saveFailed: "Could not update the chat title.",
      },
    },
    deleteConfirm: {
      title: "Delete chat?",
      message: "This chat and all its messages will be permanently deleted.",
      cancel: "Cancel",
      confirm: "Delete",
      closeAria: "Close",
      errors: {
        deleteFailed: "Could not delete the chat.",
      },
    },
  },
} as const;
